import { useRef, useState } from "react";
import { api, fmtBRL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, ArrowLeft, CheckCircle2, AlertTriangle, XCircle, Info, Loader2 } from "lucide-react";
import { ImportTemplateCard } from "@/components/import/ImportTemplateCard";

// ImportDialog — Fluxo em 2 etapas de importação de entregas (CSV/XLSX).
// 1) Upload: usuário escolhe arquivo → POST /entregas/import/preview
// 2) Preview: revisa tabela com troco calculado, marca/desmarca linhas, corrige avisos, confirma
//    → POST /entregas/import/commit → insere no banco.

const HEADER_HELP = [
  { col: "cliente_nome", alias: "cliente, nome" },
  { col: "numero_pedido", alias: "pedido, nº pedido" },
  { col: "cliente_telefone", alias: "telefone, celular, whatsapp" },
  { col: "bairro", alias: "" },
  { col: "tipo_entrega", alias: "tipo, tipo de entrega" },
  { col: "forma_pgto", alias: "forma, pagamento, pgto" },
  { col: "valor_pedido", alias: "valor, preço, total" },
  { col: "valor_pago", alias: "troco_para, pago" },
];

export function ImportDialog({ open, onOpenChange, onImported }) {
  const [step, setStep] = useState("upload"); // upload | preview | committing
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [preview, setPreview] = useState(null); // { rows, summary, unknown, catalogs }
  const [selected, setSelected] = useState(new Set()); // linhas marcadas para importar
  const inputRef = useRef(null);

  const reset = () => {
    setStep("upload"); setFile(null); setPreview(null); setSelected(new Set());
    if (inputRef.current) inputRef.current.value = "";
  };
  const close = () => { onOpenChange(false); setTimeout(reset, 250); };

  const onFileChange = (e) => { setFile(e.target.files?.[0] || null); };

  const runPreview = async () => {
    if (!file) { toast.error("Selecione um arquivo primeiro"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/entregas/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      // Seleciona por padrão apenas as linhas válidas
      const sel = new Set(r.data.rows.filter((x) => x.is_valid).map((x) => x.row_num));
      setSelected(sel);
      setStep("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao pré-visualizar arquivo");
    } finally { setUploading(false); }
  };

  const toggleRow = (rowNum) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(rowNum)) s.delete(rowNum); else s.add(rowNum);
      return s;
    });
  };
  const toggleAll = () => {
    if (!preview) return;
    const allValid = preview.rows.filter((x) => x.is_valid).map((x) => x.row_num);
    const allSelected = allValid.every((n) => selected.has(n));
    setSelected(allSelected ? new Set() : new Set(allValid));
  };

  const commit = async () => {
    if (!preview) return;
    const rowsToImport = preview.rows.filter((x) => selected.has(x.row_num) && x.is_valid);
    if (rowsToImport.length === 0) { toast.error("Marque ao menos uma linha válida"); return; }
    setCommitting(true);
    try {
      const r = await api.post("/entregas/import/commit", { rows: rowsToImport });
      toast.success(`${r.data.imported} entrega(s) importadas com sucesso`);
      onImported?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao confirmar importação");
    } finally { setCommitting(false); }
  };

  const summary = preview?.summary;
  const selectedValid = preview ? preview.rows.filter((x) => selected.has(x.row_num) && x.is_valid).length : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="dialog-import">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Importar Entregas · {step === "upload" ? "Selecionar arquivo" : "Prévia & Confirmação"}
          </DialogTitle>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="pt-5 space-y-3">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                  <div className="text-sm text-slate-700 space-y-2 flex-1">
                    <p><b>Formatos aceitos:</b> CSV (UTF-8/Windows-1252) ou XLSX.</p>
                    <p><b>Colunas suportadas</b> (case-insensitive, aliases entre parênteses):</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono bg-white/80 p-3 rounded border border-emerald-200">
                      {HEADER_HELP.map((h) => (
                        <div key={h.col}><b className="text-emerald-800">{h.col}</b>{h.alias && <span className="text-slate-500"> ({h.alias})</span>}</div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-600">
                      Se <code>valor_pago</code> for maior que <code>valor_pedido</code> e a forma aceitar troco (Dinheiro), o <b>troco é calculado automaticamente</b>. Formas como <b>Cartão / PIX / Já Pago</b> ignoram troco.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Template download — extraído para src/components/import/ImportTemplateCard */}
            <ImportTemplateCard />

            <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-slate-300 rounded-lg py-10 cursor-pointer hover:border-emerald-500 hover:bg-emerald-50/30 transition"
                   data-testid="import-drop">
              <Upload className="w-10 h-10 text-slate-400" />
              <div className="text-sm font-semibold">{file ? file.name : "Clique para escolher um arquivo (.csv ou .xlsx)"}</div>
              {file && <div className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</div>}
              <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" hidden onChange={onFileChange} data-testid="import-file-input" />
            </label>

            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={close}>Cancelar</Button>
              <Button onClick={runPreview} disabled={!file || uploading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-import-preview">
                {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analisando…</> : <><Upload className="w-4 h-4 mr-2" />Pré-visualizar</>}
              </Button>
            </div>
          </div>
        )}

        {step === "preview" && preview && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Card><CardContent className="pt-4 pb-3"><div className="text-xs text-muted-foreground">Total</div><div className="text-2xl font-bold" data-testid="sum-total">{summary.total}</div></CardContent></Card>
              <Card><CardContent className="pt-4 pb-3"><div className="text-xs text-muted-foreground">Válidas</div><div className="text-2xl font-bold text-emerald-700" data-testid="sum-valid">{summary.valid}</div></CardContent></Card>
              <Card><CardContent className="pt-4 pb-3"><div className="text-xs text-muted-foreground">Com avisos</div><div className="text-2xl font-bold text-amber-700" data-testid="sum-warn">{summary.with_warnings}</div></CardContent></Card>
              <Card><CardContent className="pt-4 pb-3"><div className="text-xs text-muted-foreground">Com erros</div><div className="text-2xl font-bold text-rose-700" data-testid="sum-error">{summary.with_errors}</div></CardContent></Card>
            </div>

            {(preview.unknown.unknown_formas.length > 0 || preview.unknown.unknown_tipos.length > 0 || preview.unknown.unknown_bairros.length > 0) && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="pt-4 pb-4 space-y-2 text-sm">
                  <div className="font-semibold text-amber-900 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Itens não cadastrados nesta empresa</div>
                  {preview.unknown.unknown_formas.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-rose-800">Formas de Pagamento (bloqueia importação):</div>
                      <div className="flex flex-wrap gap-1 mt-1" data-testid="unknown-formas">
                        {preview.unknown.unknown_formas.map((n) => <Badge key={n} className="bg-rose-100 text-rose-900 border-rose-300">{n}</Badge>)}
                      </div>
                      <div className="text-[11px] text-slate-600 mt-1">
                        Cadastradas: {preview.catalogs.formas.map((n) => <code key={n} className="mx-0.5 px-1 bg-slate-100 rounded text-[10px]">{n}</code>)}
                      </div>
                    </div>
                  )}
                  {preview.unknown.unknown_tipos.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-amber-800">Tipos de Entrega (importado em branco):</div>
                      <div className="flex flex-wrap gap-1 mt-1" data-testid="unknown-tipos">
                        {preview.unknown.unknown_tipos.map((n) => <Badge key={n} className="bg-amber-100 text-amber-900 border-amber-300">{n}</Badge>)}
                      </div>
                    </div>
                  )}
                  {preview.unknown.unknown_bairros.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-amber-800">Bairros (importado em branco):</div>
                      <div className="flex flex-wrap gap-1 mt-1" data-testid="unknown-bairros">
                        {preview.unknown.unknown_bairros.slice(0, 20).map((n) => <Badge key={n} className="bg-amber-100 text-amber-900 border-amber-300">{n}</Badge>)}
                        {preview.unknown.unknown_bairros.length > 20 && <span className="text-xs text-slate-500">+{preview.unknown.unknown_bairros.length - 20}</span>}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-xs" data-testid="import-preview-table">
                  <thead className="bg-slate-50 border-b sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-2 text-left w-8">
                        <Checkbox
                          checked={preview.rows.every((x) => !x.is_valid || selected.has(x.row_num))}
                          onCheckedChange={toggleAll}
                          data-testid="import-toggle-all"
                        />
                      </th>
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">Cliente</th>
                      <th className="px-2 py-2 text-left">Pedido</th>
                      <th className="px-2 py-2 text-left">Bairro</th>
                      <th className="px-2 py-2 text-left">Tipo</th>
                      <th className="px-2 py-2 text-left">Forma</th>
                      <th className="px-2 py-2 text-right">Valor</th>
                      <th className="px-2 py-2 text-right">Pago</th>
                      <th className="px-2 py-2 text-right">Troco</th>
                      <th className="px-2 py-2 text-left">Avisos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.length === 0 && (
                      <tr><td colSpan={11} className="text-center py-8 text-muted-foreground">Nenhuma linha detectada no arquivo</td></tr>
                    )}
                    {preview.rows.map((r) => {
                      const invalid = !r.is_valid;
                      return (
                        <tr key={r.row_num} className={`border-b ${invalid ? "bg-rose-50/70" : r.warnings.length ? "bg-amber-50/40" : "hover:bg-slate-50"}`} data-testid={`import-row-${r.row_num}`}>
                          <td className="px-2 py-1.5">
                            <Checkbox
                              checked={selected.has(r.row_num)}
                              disabled={invalid}
                              onCheckedChange={() => toggleRow(r.row_num)}
                              data-testid={`import-check-${r.row_num}`}
                            />
                          </td>
                          <td className="px-2 py-1.5 text-slate-500">{r.row_num}</td>
                          <td className="px-2 py-1.5 font-semibold truncate max-w-[140px]" title={r.cliente_nome || ""}>{r.cliente_nome || <span className="text-slate-400 italic">—</span>}</td>
                          <td className="px-2 py-1.5">{r.numero_pedido || "—"}</td>
                          <td className="px-2 py-1.5">{r.bairro_nome || (r.bairro_input ? <span className="text-amber-700 italic">{r.bairro_input}?</span> : "—")}</td>
                          <td className="px-2 py-1.5">{r.tipo_nome || (r.tipo_input ? <span className="text-amber-700 italic">{r.tipo_input}?</span> : "—")}</td>
                          <td className="px-2 py-1.5">
                            {r.forma_pgto_nome ? (
                              <span className="font-semibold">{r.forma_pgto_nome}</span>
                            ) : r.forma_pgto_input ? (
                              <span className="text-rose-700 italic font-semibold">{r.forma_pgto_input}?</span>
                            ) : "—"}
                          </td>
                          <td className="px-2 py-1.5 text-right mono">{r.valor > 0 ? fmtBRL(r.valor) : "—"}</td>
                          <td className="px-2 py-1.5 text-right mono text-slate-600">{r.valor_pago > 0 ? fmtBRL(r.valor_pago) : "—"}</td>
                          <td className="px-2 py-1.5 text-right mono font-bold">
                            {r.troco > 0 ? <span className="text-emerald-700">{fmtBRL(r.troco)}</span> : (r.aceita_troco ? "—" : <span className="text-slate-400 text-[10px]">sem troco</span>)}
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex flex-col gap-0.5">
                              {r.errors.map((w, i) => <span key={`e${i}`} className="inline-flex items-center gap-1 text-[10px] text-rose-700"><XCircle className="w-3 h-3" />{w}</span>)}
                              {r.warnings.map((w, i) => <span key={`w${i}`} className="inline-flex items-center gap-1 text-[10px] text-amber-700"><AlertTriangle className="w-3 h-3" />{w}</span>)}
                              {r.is_valid && r.warnings.length === 0 && <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700"><CheckCircle2 className="w-3 h-3" />OK</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex gap-2 justify-between items-center pt-2 border-t">
              <Button variant="ghost" onClick={() => setStep("upload")} data-testid="btn-import-back">
                <ArrowLeft className="w-4 h-4 mr-2" />Voltar
              </Button>
              <div className="flex gap-2 items-center">
                <div className="text-sm text-muted-foreground">
                  <b className="text-emerald-700" data-testid="selected-count">{selectedValid}</b> de {summary.valid} válidas marcadas
                </div>
                <Button variant="outline" onClick={close}>Cancelar</Button>
                <Button
                  onClick={commit}
                  disabled={committing || selectedValid === 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-import-commit"
                >
                  {committing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importando…</> : <><CheckCircle2 className="w-4 h-4 mr-2" />Confirmar {selectedValid} entrega(s)</>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
